import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';

import type { NavigationScreenOAuthAuthorize } from '@gitbook/app-navigation';
import { getOAuthZoneEndpoint } from '@gitbook/env';
import { Icon } from '@gitbook/spine-icons';
import {
    Actions,
    Alert,
    Button,
    ButtonSkeleton,
    Card,
    Checkbox,
    Form,
    IconSkeleton,
    IconTile,
    Page,
    PageMain,
    Submit,
    TextSkeleton,
    toast,
} from '@gitbook/spine-react';
import { NoCaptureErrorBoundary } from '@gitbook/util-debug';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { UserAvatar, useHideSplashscreen } from '../../Components';
import { apiClient, useCurrentUser } from '../../state';
import { OAuthClientIdentity } from './OAuthClientIdentity';
import { OAuthScopeSelector } from './OAuthScopeSelector';
import { getScopesForAccessLevel, type OAuthConsentFormValues } from './scopes';

type OAuthConsentSessionDetails = {
    id: string;
    client: {
        id: string;
        name: string;
        uri: string | null;
        logoUri: string | null;
        tosUri: string | null;
        policyUri: string | null;
        verified: boolean;
    };
    redirectUri: string;
    scopes: string[];
    expiresAt: string;
};

type OAuthDecision = 'approved' | 'rejected';

/**
 * Consent screen shown when a GitBook OAuth client asks the signed-in user for access.
 */
export function OAuthAuthorizeScreen(props: {
    screen: NavigationScreenOAuthAuthorize;
}): React.ReactElement {
    useHideSplashscreen(0);
    const sessionId = props.screen.sessionId ?? null;

    if (!sessionId) {
        return (
            <OAuthAuthorizeErrorScreen loadError="The authorization request is missing session information." />
        );
    }

    return (
        <NoCaptureErrorBoundary
            resetKeys={[sessionId]}
            fallbackRender={({ error }) => (
                <OAuthAuthorizeErrorScreen
                    loadError={
                        error instanceof Error
                            ? error.message
                            : 'The authorization request could not be loaded.'
                    }
                />
            )}
        >
            <React.Suspense fallback={<OAuthAuthorizeScreenSkeleton />}>
                <OAuthAuthorizeScreenContent sessionId={sessionId} />
            </React.Suspense>
        </NoCaptureErrorBoundary>
    );
}

function OAuthAuthorizeScreenContent(props: { sessionId: string }): React.ReactElement {
    const { sessionId } = props;
    const currentUser = useCurrentUser();
    const [pendingDecision, setPendingDecision] = React.useState<OAuthDecision | null>(null);
    const { data: sessionDetails } = useSuspenseQuery({
        queryKey: ['oauth', 'authorization-session', sessionId],
        queryFn: () => fetchOAuthConsentSession(sessionId),
        retry: false,
    });

    const verified = sessionDetails.client.verified;

    const requestedScopes = sessionDetails.scopes;

    // Read only is the default access level, so the user starts by granting the least privileged
    // subset of what the client requested and can opt up to full access or customize.
    const form = useForm<OAuthConsentFormValues>({
        defaultValues: {
            scopes: getScopesForAccessLevel('read', requestedScopes),
            trusted: false,
        },
    });

    const completeAuthorization = useEventCallback(
        async (decision: OAuthDecision, grantedScopes?: string[]) => {
            if (pendingDecision) {
                return;
            }

            setPendingDecision(decision);
            try {
                const callbackURL = new URL('/app/callback', getOAuthZoneEndpoint());
                callbackURL.searchParams.set('sessionId', sessionId);
                callbackURL.searchParams.set('decision', decision);
                // Only the scopes the user kept selected are granted (downscoping); the signed URL
                // makes this tamper-proof.
                if (decision === 'approved' && grantedScopes) {
                    callbackURL.searchParams.set('grantedScopes', grantedScopes.join(' '));
                }

                const { data } = await apiClient.internal.signServiceUrl({
                    url: callbackURL.toString(),
                });
                window.location.assign(data.url.toString());
            } catch (error) {
                setPendingDecision(null);
                toast.error('Authorization failed', null, {
                    description:
                        error instanceof Error
                            ? error.message
                            : 'The authorization request could not be completed.',
                });
            }
        }
    );

    const isCompletingAuthorization = Boolean(pendingDecision);

    return (
        <OAuthAuthorizeLayout
            intro={
                <OAuthAuthorizeIntro clientName={sessionDetails.client.name} user={currentUser} />
            }
        >
            <Form
                form={form}
                onSubmit={form.handleSubmit((values) =>
                    completeAuthorization('approved', values.scopes)
                )}
            >
                <div className="p-5">
                    <OAuthClientIdentity
                        client={sessionDetails.client}
                        redirectUri={sessionDetails.redirectUri}
                        verified={verified}
                    />
                </div>
                {requestedScopes.length > 0 ? (
                    <div className="border-t p-5">
                        <OAuthScopeSelector
                            requestedScopes={requestedScopes}
                            disabled={isCompletingAuthorization}
                        />
                    </div>
                ) : null}
                <div className="border-t p-5">
                    <OAuthAuthorizeFooter
                        verified={verified}
                        pendingDecision={pendingDecision}
                        onDeny={() => completeAuthorization('rejected')}
                    />
                </div>
            </Form>
        </OAuthAuthorizeLayout>
    );
}

/**
 * Layout of the GitBook OAuth consent screen.
 */
function OAuthAuthorizeLayout(props: {
    intro: React.ReactNode;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <Page className="min-h-full bg-side">
            <PageMain className="min-h-screen items-center justify-center px-6 py-12">
                <div className="flex w-full flex-col items-center gap-10 lg:flex-row lg:items-start lg:justify-center lg:gap-10">
                    <div className="flex w-full max-w-sm flex-col gap-6 lg:pt-6">{props.intro}</div>
                    <Card className="w-full max-w-lg overflow-hidden bg-neutral-surface">
                        {props.children}
                    </Card>
                </div>
            </PageMain>
        </Page>
    );
}

/**
 * Footer of the GitBook OAuth consent screen.
 */
function OAuthAuthorizeFooter(props: {
    verified: boolean;
    pendingDecision: OAuthDecision | null;
    onDeny: () => void;
}): React.ReactElement {
    const { verified, pendingDecision, onDeny } = props;
    const { setValue } = useFormContext<OAuthConsentFormValues>();
    const scopes = useWatch<OAuthConsentFormValues, 'scopes'>({ name: 'scopes' }) ?? [];
    const trusted = useWatch<OAuthConsentFormValues, 'trusted'>({ name: 'trusted' }) ?? false;

    const isCompleting = Boolean(pendingDecision);
    const allowDisabled = isCompleting || scopes.length === 0 || (!verified && !trusted);

    return (
        <div className="flex flex-wrap items-center justify-between gap-3">
            {verified ? (
                <span className="flex-1 text-muted text-ui-small">
                    You can revoke this access at any time in account settings.
                </span>
            ) : (
                <div className="flex flex-1 items-center gap-2">
                    <Checkbox
                        value={trusted}
                        onChange={(checked) => setValue('trusted', checked, { shouldDirty: true })}
                        disabled={isCompleting}
                        size="small"
                        aria-label="I recognize and trust this client"
                    />
                    <span className="text-ui-small">I recognize and trust this client</span>
                </div>
            )}
            <Actions>
                <Button
                    kind="secondary"
                    label="Deny"
                    leadingIcon={Icon.X}
                    onPress={onDeny}
                    disabled={isCompleting}
                    loadable={[pendingDecision === 'rejected']}
                />
                <Submit label="Allow" leadingIcon={Icon.Check} disabled={allowDisabled} />
            </Actions>
        </div>
    );
}

/**
 * Left-hand intro section.
 */
function OAuthAuthorizeIntro(props: {
    clientName: string;
    user: ReturnType<typeof useCurrentUser>;
}): React.ReactElement {
    return (
        <>
            <GitBookLogo />
            <h1 className="text-balance font-medium text-ui-heading-large">
                <span className="font-bold">{props.clientName}</span> wants to access your GitBook
                account
            </h1>
            {props.user ? (
                <div className="flex items-center gap-2">
                    <UserAvatar user={props.user} size={28} tooltip={false} />
                    <span className="text-muted">{props.user.displayName}</span>
                </div>
            ) : null}
        </>
    );
}

function GitBookLogo(): React.ReactElement {
    return (
        <div className="flex items-center gap-2">
            <Icon.LogoSquare size={26} />
            <span className="font-semibold text-ui-heading-small tracking-wide">GITBOOK</span>
        </div>
    );
}

function OAuthAuthorizeErrorScreen(props: { loadError: string }): React.ReactElement {
    return (
        <Page className="min-h-full bg-side">
            <PageMain suspense={false} className="min-h-screen justify-center">
                <Alert
                    kind="danger"
                    title="Authorization request unavailable"
                    details={props.loadError}
                />
            </PageMain>
        </Page>
    );
}

function OAuthAuthorizeScreenSkeleton(): React.ReactElement {
    return (
        <OAuthAuthorizeLayout
            intro={
                <>
                    <GitBookLogo />
                    <div className="flex w-full flex-col gap-2">
                        <TextSkeleton className="text-ui-heading-large" />
                        <TextSkeleton className="w-2/3 text-ui-heading-large" />
                    </div>
                </>
            }
        >
            <div className="flex items-center gap-3 p-5">
                <IconTile size={48} className="shrink-0 border-base bg-neutral-ui">
                    <IconSkeleton size={24} />
                </IconTile>
                <TextSkeleton className="w-32 text-ui-base" />
            </div>
            <div className="flex flex-col gap-3 border-t p-5">
                <TextSkeleton className="w-24 text-ui-small" />
                <OAuthAccessLevelCardSkeleton />
                <OAuthAccessLevelCardSkeleton />
                <TextSkeleton className="w-48 text-ui-small" />
            </div>
            <div className="flex justify-end gap-2 border-t p-5">
                <ButtonSkeleton label="Deny" leadingIcon />
                <ButtonSkeleton label="Allow" leadingIcon />
            </div>
        </OAuthAuthorizeLayout>
    );
}

function OAuthAccessLevelCardSkeleton(): React.ReactElement {
    return (
        <div className="flex items-center gap-4 rounded-md border p-4">
            <div className="flex gap-3">
                <IconTile size={48} className="shrink-0 border-base bg-neutral-ui">
                    <IconSkeleton size={18} className="shrink-0" />
                </IconTile>
                <div className="flex flex-col gap-1.5">
                    <TextSkeleton className="w-24 text-ui-base" />
                    <TextSkeleton className="w-56 text-ui-small" />
                </div>
            </div>
            <IconSkeleton size={20} className="ml-auto shrink-0" />
        </div>
    );
}

/**
 * Fetch the details of an OAuth consent session from the GitBook OAuth server using the provided session ID.
 */
async function fetchOAuthConsentSession(sessionId: string): Promise<OAuthConsentSessionDetails> {
    const sessionURL = new URL('/app/authorization', getOAuthZoneEndpoint());
    sessionURL.searchParams.set('sessionId', sessionId);
    const { data } = await apiClient.internal.signServiceUrl({
        url: sessionURL.toString(),
    });

    const response = await fetch(data.url.toString());
    if (!response.ok) {
        throw new Error('The authorization request is invalid or has expired.');
    }

    return response.json();
}
